import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Loader2,
  X,
  Image,
  Camera,
  Info,
  Lock
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PageHeader from '../../components/ui/PageHeader';
import { useApp } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import { ibanAPI, batfaasAPI } from '../../services/api';
import { processReceiptImage } from '../../services/ocrService';
import './IBANVerification.css';

// OCR verification status constants
const OCR_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  MANUAL_REVIEW: 'manual_review',
  NAME_MISMATCH: 'name_mismatch'
};

// Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

function IBANVerification() {
  const navigate = useNavigate();
  const { currentTransaction, setCurrentTransaction, addPendingRegistration } = useApp();
  const { getBrokerPath } = useBroker();
  const fileInputRef = useRef(null);

  const [iban, setIban] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [verificationState, setVerificationState] = useState('idle'); // idle, uploading, processing, verifying, success, error
  const [ocrProgress, setOcrProgress] = useState(0);
  const [verificationResult, setVerificationResult] = useState(null);

  // Redirect if no transaction
  useEffect(() => {
    if (!currentTransaction || !currentTransaction.selectedBank) {
      navigate(getBrokerPath('/'));
    }
  }, [currentTransaction, navigate, getBrokerPath]);

  // Check if selected bank is a digital wallet (uses phone number instead of IBAN)
  const isDigitalWallet = currentTransaction?.bankType === 'wallet' ||
    ['SADAPAY', 'NAYAPAY', 'EASYPAISA', 'JAZZCASH'].includes(currentTransaction?.bankCode);

  // Format IBAN or phone number as user types
  const handleIbanChange = (e) => {
    if (isDigitalWallet) {
      // For digital wallets, accept 11-digit Pakistani phone number
      let value = e.target.value.replace(/[^\d]/g, '');
      if (value.length > 11) value = value.slice(0, 11);
      setIban(value);
      setAccountNumber(value); // Phone number is also the account number
      setErrors(prev => ({ ...prev, iban: '' }));
    } else {
      // For traditional banks, format as IBAN
      let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

      // Format with spaces every 4 characters
      let formatted = '';
      for (let i = 0; i < value.length && i < 24; i++) {
        if (i > 0 && i % 4 === 0) formatted += ' ';
        formatted += value[i];
      }

      setIban(formatted);
      setErrors(prev => ({ ...prev, iban: '' }));

      // Auto-extract account number from IBAN
      if (value.length >= 8) {
        const extractedAccount = ibanAPI.extractAccountNumber(value);
        setAccountNumber(extractedAccount);
      }
    }
  };

  const handleAccountChange = (e) => {
    let value = e.target.value.replace(/[^\d]/g, '');
    if (value.length > 16) value = value.slice(0, 16);
    setAccountNumber(value);
    setErrors(prev => ({ ...prev, accountNumber: '' }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, file: 'File size must be less than 10MB' }));
        return;
      }

      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, file: 'Please select an image file (PNG, JPG)' }));
        return;
      }

      setSelectedFile(file);
      setErrors(prev => ({ ...prev, file: '' }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      const event = { target: { files: [file] } };
      handleFileSelect(event);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVerify = async () => {
    const newErrors = {};

    // Validate IBAN or Phone Number
    const cleanIban = iban.replace(/\s/g, '');

    if (isDigitalWallet) {
      // For digital wallets, validate as Pakistani phone number (11 digits starting with 03)
      const phonePattern = /^03\d{9}$/;
      if (!phonePattern.test(cleanIban)) {
        newErrors.iban = 'Please enter a valid Pakistani phone number (11 digits starting with 03)';
      }
    } else {
      // For traditional banks, validate as IBAN
      const ibanValidation = ibanAPI.validateFormat(cleanIban);
      if (!ibanValidation.valid) {
        newErrors.iban = ibanValidation.error;
      }
    }

    // Validate account number (not required for digital wallets as phone = account)
    if (!isDigitalWallet && (!accountNumber || accountNumber.length < 10)) {
      newErrors.accountNumber = 'Please enter a valid account number (at least 10 digits)';
    }

    // Validate file upload (required for all account types)
    if (!selectedFile) {
      if (isDigitalWallet) {
        newErrors.file = `Please upload a screenshot of your ${currentTransaction?.bankName} app showing your account details`;
      } else {
        newErrors.file = 'Please upload your IBAN letter or cancelled cheque';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Start verification process
    setVerificationState('uploading');
    setOcrProgress(0);

    try {
      // Convert document to base64 for storage and email attachment
      let documentBase64 = null;
      try {
        documentBase64 = await fileToBase64(selectedFile);
      } catch (err) {
        console.error('Failed to convert document to base64:', err);
      }

      // For digital wallets, process the app screenshot
      if (isDigitalWallet) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setVerificationState('verifying');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Add to pending registrations for admin review (with screenshot)
        const pendingAccount = {
          id: `PR-${Date.now()}`,
          userId: currentTransaction.userId,
          cnic: currentTransaction.userCnic,
          userName: currentTransaction.userName,
          phone: currentTransaction.userPhone || '',
          email: currentTransaction.userEmail || '',
          bankCode: currentTransaction.bankCode,
          bankName: currentTransaction.bankName,
          bankType: 'wallet',
          iban: cleanIban, // Phone number for wallets
          accountNumber: cleanIban,
          accountTitle: currentTransaction.userName.toUpperCase(),
          ocrExtractedName: null,
          status: 'pending',
          ocrStatus: 'wallet_screenshot',
          submittedAt: new Date().toISOString(),
          documentUrl: null,
          reviewNote: `Digital wallet (${currentTransaction.bankName}) - App screenshot uploaded for verification`,
          // Include screenshot for admin review
          documentBase64: documentBase64,
          documentName: selectedFile.name,
          documentType: selectedFile.type,
        };

        console.log('Adding pending registration (digital wallet with screenshot):', pendingAccount);
        addPendingRegistration(pendingAccount);

        // Update transaction with wallet details and screenshot
        setCurrentTransaction({
          ...currentTransaction,
          iban: cleanIban, // Phone number for wallets
          bankAccountNumber: cleanIban, // Same as phone for wallets
          bankAccountTitle: currentTransaction.userName.toUpperCase(),
          ibanVerified: true,
          ibanDocumentUploaded: true, // Screenshot uploaded
          ocrStatus: 'wallet_screenshot',
          requiresManualReview: true,
          // Store screenshot for admin review
          ibanDocumentBase64: documentBase64,
          ibanDocumentName: selectedFile.name,
          ibanDocumentType: selectedFile.type,
        });

        setVerificationResult({
          success: true,
          accountTitle: currentTransaction.userName.toUpperCase(),
          iban: cleanIban,
          accountNumber: cleanIban,
          requiresReview: true,
        });

        setVerificationState('success');

        // Navigate to OTP verification
        setTimeout(() => {
          navigate(getBrokerPath('/otp-verification'));
        }, 2000);
        return;
      }

      // Step 1: Upload and OCR processing (for banks only)
      await new Promise(resolve => setTimeout(resolve, 500));
      setVerificationState('processing');

      let ocrResult;

      // Use real OCR for images
      ocrResult = await processReceiptImage(selectedFile, (progress) => {
        setOcrProgress(progress);
      });

      // Step 2: Verify account title matches KYC name
      const kycName = currentTransaction.userName;
      const extractedTitle = ocrResult.data?.senderName || ocrResult.data?.accountTitle || '';

      // Check if OCR was successful
      if (!ocrResult.success) {
        // OCR failed - add to manual review queue
        // Convert document to base64 for email attachment
        let documentBase64 = null;
        try {
          documentBase64 = await fileToBase64(selectedFile);
        } catch (err) {
          console.error('Failed to convert document to base64:', err);
        }

        const pendingAccount = {
          id: `PR-${Date.now()}`,
          userId: currentTransaction.userId,
          cnic: currentTransaction.userCnic,
          userName: currentTransaction.userName,
          phone: currentTransaction.userPhone || '',
          email: currentTransaction.userEmail || '',
          bankCode: currentTransaction.bankCode,
          bankName: currentTransaction.bankName,
          iban: cleanIban,
          accountNumber: accountNumber,
          accountTitle: kycName.toUpperCase(),
          ocrExtractedName: null,
          status: 'pending',
          ocrStatus: OCR_STATUS.FAILED,
          submittedAt: new Date().toISOString(),
          documentUrl: null,
          reviewNote: 'OCR processing failed - requires manual document review',
          // Include document for email attachment
          documentBase64: documentBase64,
          documentName: selectedFile.name,
          documentType: selectedFile.type,
        };

        console.log('Adding pending registration (OCR failed):', pendingAccount);
        addPendingRegistration(pendingAccount);

        // Update transaction with IBAN data for OTP flow
        setCurrentTransaction({
          ...currentTransaction,
          iban: cleanIban,
          bankAccountNumber: accountNumber,
          bankAccountTitle: kycName.toUpperCase(),
          ibanVerified: true, // Mark as verified to allow OTP flow
          ibanDocumentUploaded: true,
          ocrStatus: OCR_STATUS.FAILED,
          requiresManualReview: true,
          // Store document for email attachment
          ibanDocumentBase64: documentBase64,
          ibanDocumentName: selectedFile.name,
          ibanDocumentType: selectedFile.type,
        });

        setVerificationResult({
          success: false,
          reason: 'ocr_failed',
          message: 'Document could not be verified automatically. OTP verification required.',
        });
        setVerificationState('manual_review');

        // Redirect to OTP verification after delay
        setTimeout(() => {
          navigate(getBrokerPath('/otp-verification'));
        }, 3000);
        return;
      }

      setVerificationState('verifying');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Check name match
      const nameMatch = batfaasAPI.verifyAccountMatch(extractedTitle || kycName, kycName);

      if (!nameMatch.matched && extractedTitle && extractedTitle.length > 0) {
        // Name mismatch - add to manual review for admin to verify
        // Convert document to base64 for email attachment
        let documentBase64 = null;
        try {
          documentBase64 = await fileToBase64(selectedFile);
        } catch (err) {
          console.error('Failed to convert document to base64:', err);
        }

        const pendingAccount = {
          id: `PR-${Date.now()}`,
          userId: currentTransaction.userId,
          cnic: currentTransaction.userCnic,
          userName: currentTransaction.userName,
          phone: currentTransaction.userPhone || '',
          email: currentTransaction.userEmail || '',
          bankCode: currentTransaction.bankCode,
          bankName: currentTransaction.bankName,
          iban: cleanIban,
          accountNumber: accountNumber,
          accountTitle: extractedTitle,
          ocrExtractedName: extractedTitle,
          status: 'pending',
          ocrStatus: OCR_STATUS.NAME_MISMATCH,
          submittedAt: new Date().toISOString(),
          documentUrl: null,
          reviewNote: `Name mismatch: KYC name "${kycName}" vs Document name "${extractedTitle}"`,
          kycName: kycName,
          extractedName: extractedTitle,
          // Include document for email attachment
          documentBase64: documentBase64,
          documentName: selectedFile.name,
          documentType: selectedFile.type,
        };

        console.log('Adding pending registration (name mismatch):', pendingAccount);
        addPendingRegistration(pendingAccount);

        // Update transaction with IBAN data for OTP flow
        setCurrentTransaction({
          ...currentTransaction,
          iban: cleanIban,
          bankAccountNumber: accountNumber,
          bankAccountTitle: extractedTitle,
          ibanVerified: true, // Mark as verified to allow OTP flow
          ibanDocumentUploaded: true,
          ocrStatus: OCR_STATUS.NAME_MISMATCH,
          requiresManualReview: true,
          // Store document for email attachment
          ibanDocumentBase64: documentBase64,
          ibanDocumentName: selectedFile.name,
          ibanDocumentType: selectedFile.type,
        });

        setVerificationResult({
          success: false,
          reason: 'name_mismatch',
          kycName: kycName,
          extractedName: extractedTitle,
        });
        setVerificationState('manual_review');

        // Redirect to OTP verification after delay
        setTimeout(() => {
          navigate(getBrokerPath('/otp-verification'));
        }, 3000);
        return;
      }

      // OCR SUCCESS - Verification successful, proceed to OTP
      setVerificationResult({
        success: true,
        accountTitle: extractedTitle || kycName.toUpperCase(),
        iban: cleanIban,
        accountNumber: accountNumber,
      });

      // Update transaction with verified data and document (documentBase64 already converted at start of function)
      setCurrentTransaction({
        ...currentTransaction,
        iban: cleanIban,
        bankAccountNumber: accountNumber,
        bankAccountTitle: extractedTitle || kycName.toUpperCase(),
        ibanVerified: true,
        ibanDocumentUploaded: true,
        documentOcrData: ocrResult.data,
        ocrStatus: OCR_STATUS.SUCCESS,
        // Store document for email attachment
        ibanDocumentBase64: documentBase64,
        ibanDocumentName: selectedFile.name,
        ibanDocumentType: selectedFile.type,
      });

      setVerificationState('success');

      // Navigate to OTP verification after short delay - ONLY when OCR succeeds
      setTimeout(() => {
        navigate(getBrokerPath('/otp-verification'));
      }, 2000);

    } catch (error) {
      console.error('Verification error:', error);

      // OCR processing error - add to manual review
      // Convert document to base64 for email attachment
      let documentBase64 = null;
      try {
        if (selectedFile) {
          documentBase64 = await fileToBase64(selectedFile);
        }
      } catch (err) {
        console.error('Failed to convert document to base64:', err);
      }

      const pendingAccount = {
        id: `PR-${Date.now()}`,
        userId: currentTransaction.userId,
        cnic: currentTransaction.userCnic,
        userName: currentTransaction.userName,
        phone: currentTransaction.userPhone || '',
        email: currentTransaction.userEmail || '',
        bankCode: currentTransaction.bankCode,
        bankName: currentTransaction.bankName,
        iban: cleanIban,
        accountNumber: accountNumber,
        accountTitle: currentTransaction.userName.toUpperCase(),
        ocrExtractedName: null,
        status: 'pending',
        ocrStatus: OCR_STATUS.FAILED,
        submittedAt: new Date().toISOString(),
        documentUrl: null,
        reviewNote: `OCR error: ${error.message}`,
        // Include document for email attachment
        documentBase64: documentBase64,
        documentName: selectedFile?.name || 'document.jpg',
        documentType: selectedFile?.type || 'image/jpeg',
      };

      console.log('Adding pending registration (OCR error):', pendingAccount);
      addPendingRegistration(pendingAccount);

      // Update transaction with IBAN data for OTP flow
      setCurrentTransaction({
        ...currentTransaction,
        iban: cleanIban,
        bankAccountNumber: accountNumber,
        bankAccountTitle: currentTransaction.userName.toUpperCase(),
        ibanVerified: true, // Mark as verified to allow OTP flow
        ibanDocumentUploaded: true,
        ocrStatus: OCR_STATUS.FAILED,
        requiresManualReview: true,
        // Store document for email attachment
        ibanDocumentBase64: documentBase64,
        ibanDocumentName: selectedFile?.name || 'document.jpg',
        ibanDocumentType: selectedFile?.type || 'image/jpeg',
      });

      setVerificationResult({
        success: false,
        reason: 'ocr_error',
        message: 'Document verification encountered an error. OTP verification required.',
      });
      setVerificationState('manual_review');

      // Redirect to OTP verification after delay
      setTimeout(() => {
        navigate(getBrokerPath('/otp-verification'));
      }, 3000);
    }
  };

  if (!currentTransaction || !currentTransaction.selectedBank) {
    return null;
  }

  return (
    <div className="iban-container animate-fade-in">
      <div className="iban-main-card">
        {/* Header Section */}
        <PageHeader showBackButton={false} greeting="Verify your account" />

        {/* Content Section */}
        <div className="iban-content">
          {/* IBAN/Phone Form */}
          <div className="section-header">
            <CreditCard size={16} />
            <span>Account Details</span>
          </div>

          <div className="iban-form">
            {isDigitalWallet ? (
              <>
                <Input
                  label="Phone Number"
                  placeholder="03XX XXXXXXX"
                  value={iban}
                  onChange={handleIbanChange}
                  error={errors.iban}
                  disabled={verificationState !== 'idle' && verificationState !== 'error'}
                />
                <p className="input-hint">Enter your {currentTransaction?.bankName} registered phone number (11 digits)</p>
              </>
            ) : (
              <>
                <Input
                  label="IBAN Number"
                  placeholder="PK00 XXXX 0000 0000 0000 0000"
                  value={iban}
                  onChange={handleIbanChange}
                  error={errors.iban}
                  disabled={verificationState !== 'idle' && verificationState !== 'error'}
                />
                <p className="input-hint">Pakistan IBAN: 24 characters starting with PK</p>

                <Input
                  label="Account Number"
                  placeholder="0000000000000000"
                  value={accountNumber}
                  onChange={handleAccountChange}
                  error={errors.accountNumber}
                  disabled={verificationState !== 'idle' && verificationState !== 'error'}
                />
              </>
            )}
          </div>

          {/* Document Upload Section - For both banks and digital wallets */}
          <>
            <div className="section-header upload-section-header">
              <Upload size={16} />
              <span>{isDigitalWallet ? 'Upload App Screenshot' : 'Upload Document'}</span>
            </div>

            {verificationState === 'idle' || verificationState === 'error' ? (
              <>
                {!preview ? (
                  <div
                    className="upload-area"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden-input"
                    />
                    <div className="upload-icon">
                      <Upload size={40} />
                    </div>
                    {isDigitalWallet ? (
                      <>
                        <h3>Upload {currentTransaction?.bankName} App Screenshot</h3>
                        <p>Screenshot showing your account details</p>
                      </>
                    ) : (
                      <>
                        <h3>Upload IBAN Letter / Cancelled Cheque</h3>
                        <p>Click or drag to upload</p>
                      </>
                    )}
                    <p className="upload-formats">PNG, JPG up to 10MB</p>
                    <div className="upload-options">
                      <Button variant="outline" icon={Camera} size="small">
                        Take Photo
                      </Button>
                      <Button variant="outline" icon={Image} size="small">
                        Choose File
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="preview-area">
                    <div className="preview-image-container">
                      <img src={preview} alt="Document preview" className="preview-image" />
                      <button className="remove-btn" onClick={clearFile}>
                        <X size={20} />
                      </button>
                    </div>
                    <div className="file-info">
                      <FileText size={20} />
                      <span>{selectedFile?.name}</span>
                      <span className="file-size">
                        {(selectedFile?.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  </div>
                )}

                {errors.file && (
                  <div className="error-message">
                    <AlertTriangle size={18} />
                    <span>{errors.file}</span>
                  </div>
                )}

                {/* Document Requirements */}
                <div className="document-requirements">
                  <h4>{isDigitalWallet ? 'Screenshot Requirements' : 'Document Requirements'}</h4>
                  {isDigitalWallet ? (
                    <ul>
                      <li>Open your {currentTransaction?.bankName} app</li>
                      <li>Go to account details/profile section</li>
                      <li>Take a screenshot showing your name and phone number</li>
                      <li>Make sure the account holder name is clearly visible</li>
                    </ul>
                  ) : (
                    <ul>
                      <li>Account holder name must be clearly visible</li>
                      <li>IBAN or account number must be readable</li>
                      <li>Bank name/logo should be visible</li>
                      <li>Document should be recent (within 3 months)</li>
                    </ul>
                  )}
                </div>
              </>
            ) : (
                <div className="verification-progress">
                  {(verificationState === 'uploading' || verificationState === 'processing' || verificationState === 'verifying') && (
                    <div className="processing-state">
                      <div className="processing-icon">
                        <Loader2 className="spinner" size={48} />
                      </div>
                      <h3>
                        {verificationState === 'uploading' && 'Uploading Document...'}
                        {verificationState === 'processing' && 'Scanning Document with OCR...'}
                        {verificationState === 'verifying' && 'Verifying Account Details...'}
                      </h3>
                      <p>
                        {verificationState === 'uploading' && 'Please wait while we upload your document'}
                        {verificationState === 'processing' && `Extracting text from document... ${ocrProgress}%`}
                        {verificationState === 'verifying' && 'Matching account title with KYC name'}
                      </p>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: verificationState === 'uploading' ? '20%' :
                                   verificationState === 'processing' ? `${Math.max(20, ocrProgress)}%` :
                                   '90%'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {verificationState === 'success' && verificationResult && (
                    <div className="success-state">
                      <div className="success-icon">
                        <CheckCircle size={64} />
                      </div>
                      <h3>Document Verified Successfully!</h3>
                      <div className="verification-details">
                        <div className="detail-row">
                          <span className="label">Account Title</span>
                          <span className="value">{verificationResult.accountTitle}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">IBAN</span>
                          <span className="value mono">{verificationResult.iban}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Account Number</span>
                          <span className="value mono">{verificationResult.accountNumber}</span>
                        </div>
                      </div>
                      <p className="redirect-message">Proceeding to OTP verification...</p>
                    </div>
                  )}

                  {verificationState === 'manual_review' && (
                    <div className="manual-review-state">
                      <div className="manual-review-icon">
                        <AlertTriangle size={64} />
                      </div>
                      <h3>Manual Review Required</h3>
                      {verificationResult?.reason === 'name_mismatch' ? (
                        <>
                          <p>The account title in the document does not match your KYC name.</p>
                          <div className="mismatch-details">
                            <div className="detail-row">
                              <span className="label">KYC Name</span>
                              <span className="value">{verificationResult.kycName}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Document Name</span>
                              <span className="value">{verificationResult.extractedName}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p>Your document could not be verified automatically and has been submitted for manual review.</p>
                      )}
                      <div className="pending-notice">
                        <Info size={20} />
                        <div>
                          <strong>Your account is pending approval</strong>
                          <p>Our team will review your document and approve your account within 24-48 hours. You'll be notified once approved.</p>
                        </div>
                      </div>
                      <p className="redirect-message">Redirecting to your account dashboard...</p>
                    </div>
                  )}
                </div>
              )}
            </>

          {/* Action Buttons */}
          {(verificationState === 'idle' || verificationState === 'error') && verificationResult?.reason !== 'name_mismatch' && (
            <div className="action-buttons">
              <Button
                variant="primary"
                size="large"
                fullWidth
                icon={ArrowRight}
                iconPosition="right"
                onClick={handleVerify}
                disabled={!iban || !selectedFile || (!isDigitalWallet && !accountNumber)}
              >
                Verify & Continue
              </Button>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="iban-footer">
          <div className="footer-title">
            <Lock size={14} />
            <span>Secure Verification</span>
          </div>
          <p className="footer-text">Your documents are encrypted and securely processed</p>
        </div>
      </div>
    </div>
  );
}

export default IBANVerification;
